import { useState } from "react";
import {
  Link,
  Navigate,
  createFileRoute,
  useNavigate,
} from "@tanstack/react-router";

import { ApiError, auth } from "~/lib/api";
import jottyLogo from "../../../marketing/public/jotty-logo.svg";
import { desktopRequest } from "~/lib/desktop-intent";
import { useAuthStore } from "~/lib/auth-store";

export const Route = createFileRoute("/auth/sign-in")({
  component: SignInPage,
});

function SignInPage() {
  const status = useAuthStore((s) => s.status);
  const setUser = useAuthStore((s) => s.setUser);
  const navigate = useNavigate();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  if (status === "authenticated")
    return desktopRequest() ? (
      <Navigate
        to="/desktop/authorize"
        search={{ request: desktopRequest()! }}
        replace
      />
    ) : (
      <Navigate to="/" replace />
    );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const user = await auth.signIn({ email, password });
      await setUser(user);
      const request = desktopRequest();
      if (request)
        void navigate({ to: "/desktop/authorize", search: { request } });
      else void navigate({ to: "/" });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Sign-in failed");
      setSubmitting(false);
    }
  };

  return (
    <AuthShell
      title="Sign in to Jotty"
      subtitle={
        desktopRequest()
          ? "Continue to Jotty for Mac with your account."
          : "Sign in to your Jotty account."
      }
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <Field label="Email">
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="email"
            required
            className={inputCls}
          />
        </Field>
        <Field label="Password">
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="current-password"
            required
            className={inputCls}
          />
        </Field>

        {error ? (
          <p className="text-sm text-[color:var(--color-coral)]">{error}</p>
        ) : null}

        <button type="submit" disabled={submitting} className={primaryBtn}>
          {submitting
            ? "Signing in…"
            : desktopRequest()
              ? "Sign in & continue"
              : "Sign in"}
        </button>

        <div className="flex items-center justify-between text-sm">
          <Link
            to="/auth/forgot-password"
            className="text-[color:var(--color-north-star)] hover:underline"
          >
            Forgot password?
          </Link>
          <Link
            to="/auth/sign-up"
            className="text-[color:var(--color-north-star)] hover:underline"
          >
            Create an account
          </Link>
        </div>
      </form>
    </AuthShell>
  );
}

// --- shared shell + Tailwind classes (kept inline; M4 swaps these for Zyra) ---

export function AuthShell({
  title,
  subtitle,
  children,
}: {
  title: string;
  subtitle?: string;
  children?: React.ReactNode;
}) {
  return (
    <main className="flex min-h-screen items-center justify-center bg-[color:var(--color-surface-2)] p-7">
      <div className="flex w-full max-w-[520px] flex-col gap-5 rounded-[20px] border border-[color:var(--color-border)] bg-white px-10 py-8">
        <div className="flex h-9 items-center gap-2.5">
          <img src={jottyLogo} alt="" className="h-8 w-8" />
          <span className="font-mono text-xl font-bold">Jotty</span>
        </div>
        <div>
          <h1 className="text-[30px] font-medium leading-[38px] tracking-[-0.02em] text-[color:var(--color-text)]">
            {title}
          </h1>
          {subtitle ? (
            <p className="mt-2 text-[15px] leading-[22px] text-[color:var(--color-text-subtle)]">
              {subtitle}
            </p>
          ) : null}
        </div>
        {children}
      </div>
    </main>
  );
}

export function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children?: React.ReactNode;
}) {
  return (
    <label className="block text-sm">
      <span className="mb-1 block font-medium text-[color:var(--color-abyssal)]">
        {label}
      </span>
      {children}
      {hint ? (
        <span className="mt-1 block text-xs text-[color:var(--color-abyssal)]/60">
          {hint}
        </span>
      ) : null}
    </label>
  );
}

export const inputCls =
  "w-full rounded-md border border-[color:var(--color-abyssal)]/15 bg-white px-3 py-2 outline-none focus:border-[color:var(--color-north-star)]";

export const primaryBtn =
  "w-full rounded-md bg-[color:var(--color-north-star)] px-4 py-2.5 font-medium text-white disabled:opacity-50";
